// components/Survey/SurveyPreview.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { surveyService } from '../../services/surveyService';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';

const SurveyPreview = ({ isPublic = false }) => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCompletion, setShowCompletion] = useState(false);
  
  const topRef = useRef(null);

  const navigateToLinks = () => {
    navigate(`/survey-links/${surveyId}`);
  };

  const navigateToSurveys = () => {
    navigate('/surveys');
  };

  useEffect(() => {
    if (surveyId) {
      loadSurveyPreview();
    }
  }, [surveyId]);

  useEffect(() => {
    scrollToTop();
  }, [currentCategoryIndex]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const loadSurveyPreview = async () => {
    try {
      console.log('🔄 Starting to load survey preview for ID:', surveyId);
      setLoading(true);
      setError('');
      
      const previewData = await surveyService.getSurveyPreview(surveyId);
      console.log('✅ Survey preview data received:', previewData);
      
      if (previewData && previewData.survey) {
        console.log('✅ Found survey data:', previewData.survey);
        
        const normalizedSurvey = {
          Id: previewData.survey.id,
          Name: previewData.survey.name,
          Description: previewData.survey.description,
          Categories: (previewData.survey.categories || []).map(category => ({
            Id: category.id,
            Name: category.name,
            Description: category.description,
            Order: category.order,
            Questions: (category.questions || []).map(question => ({
              Id: question.id,
              QuestionText: question.questionText,
              Type: question.type,
              Order: question.order,
              IsRequired: question.isRequired,
              Placeholder: question.placeholder,
              HelpText: question.helpText,
              Options: question.options,
              MinValue: question.minValue,
              MaxValue: question.maxValue
            }))
          }))
        };
        
        console.log('🔄 Normalized survey data:', normalizedSurvey);
        
        if (normalizedSurvey.Name && Array.isArray(normalizedSurvey.Categories)) {
          setSurvey(normalizedSurvey);
        } else {
          throw new Error('Survey data missing required properties after normalization');
        }
      } else {
        console.warn('⚠️ No survey data found in response structure');
        throw new Error('No survey data found in the response');
      }
    } catch (err) {
      console.error('❌ Error loading survey preview:', err);
      setError(err.message || 'Failed to load survey preview');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const nextCategory = () => {
    if (survey && currentCategoryIndex < survey.Categories.length - 1) {
      setCurrentCategoryIndex(prev => prev + 1);
    } else {
      setShowCompletion(true);
    }
  };

  const prevCategory = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(prev => prev - 1);
    }
  };

  const resetSurvey = () => {
    setCurrentCategoryIndex(0);
    setAnswers({});
    setShowCompletion(false);
    scrollToTop();
  };

  const getProgressPercentage = () => {
    if (!survey || !survey.Categories.length) return 0;
    return ((currentCategoryIndex + 1) / survey.Categories.length) * 100;
  };

  const getTotalQuestions = () => {
    if (!survey?.Categories) return 0;
    return survey.Categories.reduce((total, category) => 
      total + (category.Questions?.length || 0), 0
    );
  };

  const getAnsweredQuestions = () => {
    return Object.keys(answers).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900">Loading Survey Preview...</h2>
          <p className="text-gray-600 mt-2">Survey ID: {surveyId}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <QuestionMarkCircleIcon className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Failed to Load Survey</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <button 
              onClick={loadSurveyPreview}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Try Again
            </button>
            {!isPublic && (
              <button 
                onClick={navigateToSurveys}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Back to Surveys
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-blue-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <DocumentTextIcon className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Survey Not Found</h3>
          <p className="text-gray-600 mb-6">The requested survey could not be loaded.</p>
          {!isPublic && (
            <button 
              onClick={navigateToSurveys}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Back to Surveys
            </button>
          )}
        </div>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-green-200 p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Survey Complete!</h2>
          <p className="text-gray-600 mb-6">Thank you for completing the survey.</p>
          
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="text-2xl font-bold text-blue-600">{getAnsweredQuestions()}</div>
              <div className="text-sm text-blue-700">Questions Answered</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="text-2xl font-bold text-green-600">{getTotalQuestions()}</div>
              <div className="text-sm text-green-700">Total Questions</div>
            </div>
          </div>

          <div className="space-y-3">
            {isPublic ? (
              <p className="text-gray-600 bg-gray-50 p-4 rounded-lg border border-gray-200">
                Your responses have been recorded. Thank you for your feedback!
              </p>
            ) : (
              <>
                <button 
                  onClick={resetSurvey}
                  className="w-full px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Restart Preview
                </button>
                <button 
                  onClick={navigateToLinks}
                  className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  🔗 Manage Survey Links
                </button>
                <button 
                  onClick={navigateToSurveys}
                  className="w-full px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Back to Surveys
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!survey.Categories || survey.Categories.length === 0 || !survey.Categories[currentCategoryIndex]) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-amber-200 p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <QuestionMarkCircleIcon className="w-8 h-8 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Categories Found</h3>
          <p className="text-gray-600 mb-6">This survey doesn't have any categories configured.</p>
          <div className="flex flex-col gap-3">
            <button 
              onClick={loadSurveyPreview}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Reload Survey
            </button>
            {!isPublic && (
              <button 
                onClick={navigateToLinks}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Back to Survey Links
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentCategory = survey.Categories[currentCategoryIndex];
  const totalQuestions = getTotalQuestions();
  const answeredQuestions = getAnsweredQuestions();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex flex-col">
      <div ref={topRef} />
      
      {!isPublic && (
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10 flex-shrink-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={navigateToLinks}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
                Back to Links
              </button>
              <div className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                Preview Mode
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content area that grows and scrolls */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Survey Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">{survey.Name}</h1>
              {survey.Description && (
                <p className="text-gray-600 text-lg">{survey.Description}</p>
              )}
            </div>
            
            {/* Progress Bar */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>Category {currentCategoryIndex + 1} of {survey.Categories.length}</span>
                  {!isPublic && (
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                      {answeredQuestions}/{totalQuestions} answered
                    </span>
                  )}
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {Math.round(getProgressPercentage())}% Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Current Category */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-blue-500 text-white rounded-lg w-12 h-12 flex items-center justify-center text-lg font-bold flex-shrink-0">
                {currentCategory.Order}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 break-words">{currentCategory.Name}</h2>
                {currentCategory.Description && (
                  <p className="text-gray-600 break-words">{currentCategory.Description}</p>
                )}
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-6">
              {currentCategory.Questions && currentCategory.Questions.length > 0 ? (
                currentCategory.Questions.map((question, qIndex) => (
                  <QuestionItem
                    key={question.Id}
                    question={question}
                    questionNumber={qIndex + 1}
                    categoryNumber={currentCategory.Order}
                    value={answers[question.Id]}
                    onChange={(value) => handleAnswerChange(question.Id, value)}
                  />
                ))
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                  <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No questions in this category yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 sticky bottom-0 z-10">
            <div className="flex items-center justify-between">
              <button 
                className="flex items-center gap-2 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={prevCategory}
                disabled={currentCategoryIndex === 0}
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Previous
              </button>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">
                  {currentCategory.Order}
                </span>
                <span className="text-gray-400">/</span>
                <span>{survey.Categories.length}</span>
              </div>

              {currentCategoryIndex < survey.Categories.length - 1 ? (
                <button 
                  className="flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  onClick={nextCategory}
                >
                  Next
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              ) : (
                <button 
                  className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  onClick={nextCategory}
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  Complete Survey
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// FIXED Question Component with responsive layout
const QuestionItem = ({ question, questionNumber, categoryNumber, value, onChange }) => {
  const renderInput = () => {
    const baseProps = {
      placeholder: question.Placeholder || 'Enter your answer...',
      className: 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'
    };

    switch (question.Type) {
      case 0: // Heading
        return (
          <div className="text-xl font-bold text-gray-900 py-4 border-b border-gray-200 break-words">
            {question.QuestionText}
          </div>
        );

      case 1: // RatingScale - FIXED RESPONSIVE VERSION
        const ratingLabels = [
          "Not at all likely",
          "Slightly likely", 
          "Moderately likely",
          "Very likely",
          "Extremely likely"
        ];

        const ratingColors = [
          'bg-red-100 border-red-300 text-red-800',
          'bg-orange-100 border-orange-300 text-orange-800',
          'bg-yellow-100 border-yellow-300 text-yellow-800',
          'bg-blue-100 border-blue-300 text-blue-800',
          'bg-green-100 border-green-300 text-green-800'
        ];

        const selectedRatingColors = [
          'bg-red-500 border-red-600 text-white',
          'bg-orange-500 border-orange-600 text-white',
          'bg-yellow-500 border-yellow-600 text-white',
          'bg-blue-500 border-blue-600 text-white',
          'bg-green-500 border-green-600 text-white'
        ];

        return (
          <div className="w-full overflow-hidden">
            <div className="grid grid-cols-5 gap-1 sm:gap-2 mb-2 sm:mb-3">
              {[1, 2, 3, 4, 5].map((rating) => (
                <label key={rating} className="flex flex-col items-center cursor-pointer">
                  <input
                    type="radio"
                    name={`question-${question.Id}`}
                    value={rating}
                    checked={value === rating}
                    onChange={() => onChange(rating)}
                    className="sr-only"
                  />
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg border-2 flex items-center justify-center font-semibold text-sm transition-all ${
                    value === rating 
                      ? `${selectedRatingColors[rating-1]} shadow-md scale-105` 
                      : `${ratingColors[rating-1]} hover:shadow-md hover:scale-105`
                  }`}>
                    {rating}
                  </div>
                </label>
              ))}
            </div>
            
            <div className="grid grid-cols-5 gap-1 sm:gap-2">
              {ratingLabels.map((label, index) => (
                <span 
                  key={index} 
                  className="text-[10px] xs:text-xs text-gray-600 text-center leading-tight font-medium break-words px-0.5"
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        );

      case 2: // TextField
        return (
          <input 
            type="text" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 3: // DateField
        return (
          <input 
            type="date" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 4: // TextArea
        return (
          <textarea 
            rows={4} 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={`${baseProps.className} resize-vertical min-h-[100px]`}
          />
        );

      case 5: // RadioButtons
        return (
          <div className="space-y-3">
            {question.Options?.map((option, index) => (
              <label key={index} className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name={`question-${question.Id}`}
                  value={option}
                  checked={value === option}
                  onChange={() => onChange(option)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700 break-words">{option}</span>
              </label>
            ))}
          </div>
        );

      case 6: // HiddenField
        return (
          <input 
            type="hidden" 
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 7: // NumberField
        return (
          <input 
            type="number" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            min={question.MinValue}
            max={question.MaxValue}
          />
        );

      case 8: // EmailField
        return (
          <input 
            type="email" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 9: // Checkboxes
        const selectedValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-3">
            {question.Options?.map((option, index) => (
              <label key={index} className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  value={option}
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValue = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter(v => v !== option);
                    onChange(newValue);
                  }}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="text-gray-700 break-words">{option}</span>
              </label>
            ))}
          </div>
        );

      case 10: // SelectList
        return (
          <select 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select an option</option>
            {question.Options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 11: // FileUpload
        return (
          <input 
            type="file" 
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            onChange={(e) => {
              const file = e.target.files[0];
              onChange(file ? file.name : '');
            }}
          />
        );

      case 12: // NetPromoterScore
        return (
          <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
            <div className="flex justify-between text-xs sm:text-sm text-gray-600 mb-4">
              <span className="break-words">Not at all likely (0)</span>
              <span className="break-words">Extremely likely (10)</span>
            </div>
            <div className="flex justify-center gap-1 sm:gap-2 overflow-x-auto pb-2">
              {Array.from({ length: 11 }, (_, i) => i).map((score) => (
                <label key={score} className="flex flex-col items-center gap-1 cursor-pointer flex-shrink-0">
                  <input
                    type="radio"
                    name={`question-${question.Id}`}
                    value={score}
                    checked={value === score}
                    onChange={() => onChange(score)}
                    className="sr-only"
                  />
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 flex items-center justify-center text-xs sm:text-sm font-medium transition-all ${
                    value === score 
                      ? 'bg-blue-500 text-white border-blue-500' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                  }`}>
                    {score}
                  </div>
                </label>
              ))}
            </div>
          </div>
        );

      case 13: // Signature
        return (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
            <p className="text-gray-600 mb-2">Signature field - Click to sign</p>
            <button 
              type="button"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              onClick={() => onChange('Signed')}
            >
              Add Signature
            </button>
          </div>
        );

      case 14: // Paragraph
        return (
          <div className="text-gray-700 bg-gray-50 p-4 rounded-lg border border-gray-200 break-words">
            {question.QuestionText}
          </div>
        );

      default:
        return (
          <input 
            type="text" 
            {...baseProps}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
    }
  };

  const isContentOnly = question.Type === 0 || question.Type === 14;

  if (isContentOnly) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {renderInput()}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 hover:shadow-sm transition-shadow overflow-hidden">
      <div className="mb-4">
        <div className="flex items-start gap-3">
          <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium flex-shrink-0">
            {categoryNumber}.{questionNumber}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 break-words">
              {question.QuestionText}
              {question.IsRequired && <span className="text-red-500 ml-1">*</span>}
            </h3>
            {question.HelpText && (
              <p className="text-gray-600 text-sm bg-blue-50 p-3 rounded-lg border border-blue-200 break-words">
                {question.HelpText}
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="question-input-container w-full overflow-hidden">
        {renderInput()}
      </div>
    </div>
  );
};

export default SurveyPreview;